import { planAtLeast, SUMMARY_NOTE_CREDITS, SUMMARY_NOTE_MIN_MESSAGES, WORKFLOW_STEP_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { AddSummaryNoteStep } from "../types";
import { StepChannelNotes } from "./channel-notes";
import { SwitchRow } from "./switch-row";
import type { FormProps } from "./webhook-form";

const S = "engage.workflows.summaryNote";
const SUMMARY_PLAN = WORKFLOW_STEP_MIN_PLAN.add_summary_note ?? "pro";

/** Add summary note: an AI summary of the conversation, written as an internal note (Pro; one AI credit; never sent to the visitor). */
export function SummaryNoteForm({ step, disabled, onChange }: FormProps<AddSummaryNoteStep>) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const locked = plan !== undefined && !planAtLeast(plan, SUMMARY_PLAN);
  const planName = t(`console.plans.${SUMMARY_PLAN}`);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="brand">
          <Icon name="sparkles" className="size-3" />
          {t(`${S}.badge`)}
        </Badge>
        <Badge tone={locked ? "warning" : "neutral"}>{t(`${S}.planBadge`, { plan: planName })}</Badge>
      </div>
      {locked ? (
        <p className="flex items-center gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note" data-testid="summary-plan-lock">
          <Icon name="lock" className="size-4 shrink-0" />
          {t(`${S}.plan`, { plan: planName })}
        </p>
      ) : null}
      <p className="text-sm text-fg-muted">{t(`${S}.explainer`, { credits: SUMMARY_NOTE_CREDITS, min: SUMMARY_NOTE_MIN_MESSAGES })}</p>
      <SwitchRow label={t(`${S}.notify`)} description={t(`${S}.notifyHint`)} checked={step.notify_assignee} disabled={disabled} onChange={(notify_assignee) => { onChange({ ...step, notify_assignee }); }} />
      <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
        <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0" />
        {t(`${S}.privacy`)}
      </p>
      <StepChannelNotes type="add_summary_note" />
    </div>
  );
}
